import { createFilter } from 'react-select'
import { COLORS } from './constants'

export const createSquare = ({ color = '#ccc', height = 8, width = 8 }) => ({
  alignItems: 'center',
  display: 'flex',

  ':before': {
    backgroundColor: color,
    borderRadius: 2,
    content: '""',
    display: 'block',
    marginRight: 4,
    height,
    width,
  },
})

export const flattenValues = (
  values,
  path,
  level = 0,
  color,
  ancestorIds = new Set()
) => {
  const flatValues = values
    .sort((a, b) => (a.nameToDisplay > b.nameToDisplay ? 1 : -1))
    .flatMap((value) => {
      const label = value.nameToDisplay

      if (label?.length < 1) {
        return []
      }

      // A dimension value can never legitimately be its own ancestor. The
      // search-merge (combineDimensions/insertAtMatchingDimensionId) can graft a
      // linked copy of a value as a child of the node that already represents it
      // — dedup there is only by dimensionId, and linked copies carry a
      // different dimensionId — yielding a chain where the same value id repeats
      // at consecutive levels. Skip a node whose id already appears in its
      // ancestor chain so a value is never rendered nested under itself. The
      // chain is tracked per branch, so a value that legitimately appears in two
      // separate branches is still emitted.
      if (ancestorIds.has(value.id)) {
        return []
      }

      const option = {
        color,
        label,
        level,
        path: [...path],
        value: value.id,
      }

      if (value.children) {
        return [
          {
            ...option,
            path: [...path, label],
          },
        ].concat(
          flattenValues(
            value.children,
            [...path, label],
            level + 1,
            color,
            new Set(ancestorIds).add(value.id)
          )
        )
      }

      return option
    })

  return flatValues.toJS ? flatValues.toJS() : flatValues
}

// Flattens a single dimension into the { color, label, level, options, path,
// value } group shape react-select expects. flattenValues already returns plain
// (toJS'd) arrays, so the result is a plain object — no outer .toJS() needed.
const flattenDimension = (dimension, index) => {
  const label = dimension.nameToDisplay
  const level = 0
  const options = flattenValues(dimension.values, [label], level, index)
  return {
    color: index,
    label,
    level,
    options,
    path: [label],
    value: dimension.id,
  }
}

// Transforms the Immutable dimensionData into the plain array of groups
// react-select consumes. The flatten+sort+toJS work is ~43ms for ~11k options,
// so an optional per-dimension `cache` (a Map keyed by dimension id) lets
// unchanged dimensions reuse their previously flattened group. During a backend
// search typically only one dimension's values change, so this turns a full
// 43ms re-transform into re-flattening just the dimension that actually changed.
export const getDimensionDataAsArray = (dimensionData, cache) => {
  const result = []
  dimensionData.forEach((dimension, index) => {
    if (!cache) {
      result.push(flattenDimension(dimension, index))
      return
    }
    const id = dimension?.id ?? dimension?.get?.('id') ?? index
    // Include index in the signature: a dimension's color/value(index) is part
    // of its flattened output, so a reorder must invalidate the cached group.
    const signature = `${index}:${countOptionsDeep(dimension?.values)}`
    const cached = cache.get(id)
    if (cached && cached.signature === signature) {
      result.push(cached.group)
      return
    }
    const group = flattenDimension(dimension, index)
    cache.set(id, { signature, group })
    result.push(group)
  })
  return result
}

// Recursively counts values (including nested children). Handles both Immutable
// collections (.size / .get) and plain arrays/objects, since consumers pass a
// mix (e.g. EditRollingRuleView injects a plain "Blank" option). Cheap: just
// integer increments and property reads, no allocation.
export const countOptionsDeep = (values) => {
  if (!values) return 0
  let count = 0
  values.forEach((value) => {
    count += 1
    const children = value?.children ?? value?.get?.('children')
    const childCount = children?.size ?? children?.length ?? 0
    if (childCount > 0) {
      count += countOptionsDeep(children)
    }
  })
  return count
}

// True when at least one dimension's loaded value count reaches `searchLimit`,
// i.e. its page may be truncated on the server and a backend search could
// surface more. When no dimension reaches the cap, every value is already loaded
// locally and a search is pointless. Handles both Immutable and plain entries.
export const hasTruncatedDimension = (dimensionData, searchLimit) =>
  dimensionData.some(
    (dimension) =>
      countOptionsDeep(
        dimension?.get ? dimension.get('values') : dimension?.values
      ) >= searchLimit
  )

// Builds a cheap content signature for a dimensionData List so callers can skip
// the expensive getDimensionDataAsArray transform when the underlying content is
// unchanged but the reference is not (selectors / parent renders create fresh
// references every render). The signature captures each dimension's id plus its
// deep value count — exactly the quantities that change when search results are
// merged in (the measured trigger for repeated 43ms re-transforms).
export const getDimensionDataSignature = (dimensionData) => {
  if (!dimensionData) return '0'
  const parts = []
  dimensionData.forEach((dimension) => {
    const id = dimension?.id ?? dimension?.get?.('id') ?? ''
    const values = dimension?.get ? dimension.get('values') : dimension?.values
    parts.push(`${id}:${countOptionsDeep(values)}`)
  })
  return parts.join('|')
}

// Relevance rank for ordering search results: exact label match first, then a
// prefix match, then everything else (react-select's filter has already ensured
// a match somewhere in the path, so the rest keep their alphabetical order).
const matchRank = (label, term) => {
  const value = label?.toLowerCase() ?? ''
  if (value === term) return 0
  if (value.startsWith(term)) return 1
  return 2
}

// Reorders groups and the options within them so the closest name match to
// `term` surfaces first. Array.sort is stable, so equally-ranked entries keep
// their original (alphabetical) order. Returns the groups untouched when there
// is no term.
//
// Each option's rank is computed exactly once (the O(n) `.map` below) and the
// sort then compares pre-computed integers. This avoids calling matchRank — and
// the toLowerCase it does — inside the comparator, where it would run O(n log n)
// times per keystroke. The group's best rank is likewise computed once while we
// already have the ranks in hand. The temporary `rank` wrapper is stripped from
// options and the helper `_rank` from groups before returning.
export const sortOptionsByRelevance = (groups, term) => {
  if (!term) return groups
  const lower = term.toLowerCase()
  return [...groups]
    .map((group) => {
      const ranked = group.options.map((option) => ({
        option,
        rank: matchRank(option.label, lower),
      }))
      ranked.sort((a, b) => a.rank - b.rank)
      return {
        ...group,
        options: ranked.map((r) => r.option),
        _rank: ranked.reduce((best, r) => Math.min(best, r.rank), 2),
      }
    })
    .sort((a, b) => a._rank - b._rank)
    .map(({ _rank, ...group }) => group)
}

export const getCustomFilter = createFilter({
  stringify: (option) => option.data.path?.join(' '),
  ignoreAccents: false,
})

// A single value id can appear more than once in the flattened options when a
// hierarchy has two nodes sharing the same underlying dimension value id (a
// rollup and its leaf carrying the same id). Dedupe by value so a selected id
// yields exactly one chip — otherwise react-select renders duplicate
// multi-value chips with the same `label-value` key.
export const getSelectedOptions = (selectedDimensionValueIds, options) => {
  if (!(selectedDimensionValueIds?.length > 0)) return []
  const seen = new Set()
  return options.flatMap((value) =>
    value.options.filter((option) => {
      if (
        !selectedDimensionValueIds.includes(option.value) ||
        seen.has(option.value)
      ) {
        return false
      }
      seen.add(option.value)
      return true
    })
  )
}

// Set of every option value (stringified) present in the flattened groups. Lets
// callers tell which selected ids are already loaded locally vs. still need a
// server fetch.
export const getLoadedOptionValues = (options) => {
  const values = new Set()
  options.forEach((group) =>
    group.options.forEach((option) => values.add(String(option.value)))
  )
  return values
}

export const getCustomStyles = {
  container: (styles, state) => {
    if (state.selectProps?.showOnlySelectedValues) {
      return {
        ...styles,
        pointerEvents: 'auto',
      }
    }
    return { ...styles }
  },
  control: (styles, state) => {
    if (state.selectProps?.showOnlySelectedValues) {
      return {
        margin: '-5px',
      }
    }

    return {
      ...styles,
      borderColor: state.isFocused ? 'var(--bs-primary)' : styles.borderColor,
      borderRadius: 0,
      boxShadow: state.isFocused
        ? '0 0 8px rgba(var(--bs-primary-rgb), .6 )'
        : 'none',
      '&:hover': {
        borderColor: 'var(--bs-primary)',
      },
      minHeight: '34px',
    }
  },
  dropdownIndicator: (styles) => {
    return { ...styles, padding: '4px' }
  },
  clearIndicator: (styles) => {
    return { ...styles, padding: '4px' }
  },
  groupHeading: (styles, { data }) => {
    return {
      ...styles,
      fontSize: '80%',
      marginBottom: 0,
      ...createSquare({
        color: COLORS[data.color],
        height: 10,
        width: 10,
      }),
    }
  },
  indicatorsContainer: (styles, state) => {
    if (state.selectProps?.showOnlySelectedValues) {
      return {
        display: 'none',
      }
    }
    return styles
  },
  multiValue: (styles, state) => {
    if (state.selectProps?.showOnlySelectedValues) {
      return {
        ...styles,
        background: 'none',
        border: '1px solid var(--fz-secondary-border)',
        paddingRight: '4px',
        pointerEvents: 'true',
      }
    }
    return styles
  },
  multiValueLabel: (styles, { data }) => {
    if (data.color === undefined) return styles
    return {
      ...styles,
      cursor: 'pointer',
      whiteSpace: 'normal',
      ...createSquare({ color: COLORS[data.color] }),
    }
  },
  multiValueRemove: (styles, state) => {
    if (state.selectProps?.showOnlySelectedValues) {
      return {
        display: 'none',
      }
    }
    return styles
  },
  option: (styles, { data, isFocused, isSelected }) => ({
    ...styles,
    ':active': {
      backgroundColor: 'rgba(var(--bs-primary-rgb), .3)',
    },
    ':hover': {
      backgroundColor: isSelected ? 'var(--bs-primary)' : 'var(--bs-gray-200)',
      transition: 'background ease-in-out',
    },
    padding: '4px 8px',
    backgroundColor: {},
    paddingLeft: `${12 + data.level * 12}px`,
    maxWidth: '100%',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: ' break-spaces',
  }),
  valueContainer: (styles, { hasValue, selectProps }) => {
    if (selectProps?.showOnlySelectedValues) {
      return { ...styles, padding: 0 }
    }
    return {
      ...styles,
      ...(hasValue && { paddingLeft: '4px' }),
      paddingRight: 0,
    }
  },
  menu: (styles) => ({
    ...styles,
    zIndex: 10,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
    pointerEvents: 'auto',
  }),
}
